<template>
  <v-container>
    <h1 class="text-h5 mb-4">MRR Resumen</h1>

    <!-- Filtros -->
    <v-card class="mb-4 pa-4" outlined>
      <v-row align="center">
        <v-col cols="6" sm="3">
          <v-select
            v-model="selectedYear"
            :items="years"
            label="Año"
            outlined dense hide-details
            @change="load"
          />
        </v-col>
        <v-col cols="6" sm="3">
          <v-select
            v-model="selectedQuarter"
            :items="quarters"
            label="Trimestre"
            outlined dense hide-details
            @change="load"
          />
        </v-col>
        <v-col cols="12" sm="6" class="d-flex flex-column flex-sm-row justify-sm-end" style="gap:8px">
          <v-btn outlined :block="isMobile" :loading="loadingProgress" @click="load">
            <v-icon left>mdi-refresh</v-icon> Actualizar
          </v-btn>
          <v-btn v-if="!isMobile" color="primary" depressed @click="openEditDialog">
            <v-icon left>mdi-pencil</v-icon> Editar metas
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Total Global -->
    <v-card outlined class="mb-6 pa-4">
      <div class="d-flex align-center justify-space-between flex-wrap mb-3">
        <span class="subtitle-1 font-weight-bold">Total General — {{ selectedYear }} Q{{ selectedQuarter }}</span>
        <span class="pw-pct">{{ totalGeneralRow.progress }} % de la meta</span>
      </div>
      <v-progress-linear
        :value="Math.min(totalGeneralRow.progress, 100)"
        :color="progressColor(totalGeneralRow.progress)"
        height="10" rounded
        class="mb-4"
      />
      <v-row dense>
        <v-col cols="6" sm="3">
          <div class="pw-label mb-2">Meta Q</div>
          <div class="pw-kpi">USD {{ totalGeneralRow.targetUSD.toLocaleString() }}</div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="pw-label mb-2">MRR Hoy</div>
          <div
            class="pw-kpi pw-kpi--link"
            title="Ver la variación diaria del MRR"
            @click="dailyDialog = true"
          >USD {{ totalGeneralRow.actualUSD.toLocaleString() }}</div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="pw-label mb-2">Faltante</div>
          <div class="pw-kpi" :class="totalGeneralRow.remainingUSD === 0 ? 'success--text' : ''">
            {{ totalGeneralRow.remainingUSD === 0 ? '✓ Alcanzado' : 'USD ' + totalGeneralRow.remainingUSD.toLocaleString() }}
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Desktop/tablet: detalle completo por país y línea de negocio -->
    <div v-if="!isMobile">
      <div v-for="pais in paises" :key="pais" class="mb-6">
        <v-card outlined>
          <v-card-title class="subtitle-1 font-weight-bold d-flex align-center">
            <v-icon left small>mdi-flag</v-icon>
            {{ pais }} — {{ selectedYear }} Q{{ selectedQuarter }}
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="rowsByPais(pais)"
            :loading="loadingProgress"
            hide-default-footer
            disable-pagination
            no-data-text="Sin metas configuradas para este país"
          >
            <template v-slot:item.lineaNegocio="{ item }">
              <span
                class="font-weight-medium primary--text"
                style="cursor:pointer; text-decoration:underline"
                @click="openDetail(item)"
              >{{ item.lineaNegocio }}</span>
            </template>

            <template v-slot:item.targetUSD="{ item }">
              USD {{ item.targetUSD.toLocaleString() }}
            </template>

            <template v-slot:item.actualUSD="{ item }">
              USD {{ item.actualUSD.toLocaleString() }}
            </template>

            <template v-slot:item.progress="{ item }">
              <div style="min-width: 160px">
                <div class="d-flex justify-space-between mb-1">
                  <span class="pw-pct">{{ item.progress }} %</span>
                </div>
                <v-progress-linear
                  :value="Math.min(item.progress, 100)"
                  :color="progressColor(item.progress)"
                  height="8"
                  rounded
                />
              </div>
            </template>

            <template v-slot:item.remainingUSD="{ item }">
              <span :class="item.remainingUSD === 0 ? 'success--text font-weight-medium' : ''">
                {{ item.remainingUSD === 0 ? '✓ Alcanzado' : 'USD ' + item.remainingUSD.toLocaleString() }}
              </span>
            </template>

            <template v-slot:body.append>
              <tr class="pw-total-row">
                <td class="body-2 py-2">Total {{ pais }}</td>
                <td class="body-2 py-2 pw-num">USD {{ totalesByPais(pais).target.toLocaleString() }}</td>
                <td class="body-2 py-2 pw-num">USD {{ totalesByPais(pais).actual.toLocaleString() }}</td>
                <td class="py-2"></td>
                <td class="body-2 py-2 pw-num" :class="totalesByPais(pais).remaining === 0 ? 'success--text' : ''">
                  {{ totalesByPais(pais).remaining === 0 ? '✓ Alcanzado' : 'USD ' + totalesByPais(pais).remaining.toLocaleString() }}
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </div>

    <!-- Mobile: solo el total por país, sin desglose por línea de negocio -->
    <v-row v-else dense>
      <v-col v-for="pais in paises" :key="pais" cols="12" sm="6">
        <v-card outlined class="pa-3">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-bold"><v-icon small left>mdi-flag</v-icon>{{ pais }}</span>
            <span class="pw-pct">{{ progressPct(totalesByPais(pais).actual, totalesByPais(pais).target) }} %</span>
          </div>
          <v-progress-linear
            :value="Math.min(progressPct(totalesByPais(pais).actual, totalesByPais(pais).target), 100)"
            :color="progressColor(progressPct(totalesByPais(pais).actual, totalesByPais(pais).target))"
            height="8" rounded
            class="mb-2"
          />
          <div class="d-flex justify-space-between caption">
            <span>Meta: USD {{ totalesByPais(pais).target.toLocaleString() }}</span>
            <span>Hoy: USD {{ totalesByPais(pais).actual.toLocaleString() }}</span>
          </div>
          <div class="d-flex justify-space-between align-center caption mt-1">
            <span :class="totalesByPais(pais).remaining === 0 ? 'success--text font-weight-medium' : ''">
              {{ totalesByPais(pais).remaining === 0 ? '✓ Alcanzado' : 'Faltante: USD ' + totalesByPais(pais).remaining.toLocaleString() }}
            </span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog variación diaria del MRR (mismo panel que la vista MRR Daily) -->
    <v-dialog v-model="dailyDialog" max-width="1100px" :fullscreen="isMobile" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Variación diaria del MRR</span>
          <v-btn icon @click="dailyDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <!-- v-if: recrea el panel en cada apertura para que traiga datos frescos -->
          <mrr-daily-panel v-if="dailyDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog detalle MRR por línea -->
    <v-dialog v-model="detailDialog" max-width="1600px" :fullscreen="isMobile">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ detailLinea }} — {{ selectedYear }} Q{{ selectedQuarter }}</span>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-data-table
            :headers="detailHeaders"
            :items="detailRows"
            :loading="loadingDetail"
            dense
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
          >
            <template v-slot:item.producto="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <span v-on="on" style="display:block;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                    {{ item.producto || '—' }}
                  </span>
                </template>
                <span>{{ item.producto }}</span>
              </v-tooltip>
            </template>

          <template v-slot:item.mrrLocal="{ item }">
              {{ item.mrrLocal.toLocaleString() }} {{ item.moneda }}
            </template>
            <!-- Extras del contrato. El backend solo los calcula para el mes en curso,
                 que es justo el único mes que este diálogo muestra. -->
            <template v-slot:item.collectionFeeLocal="{ item }">
              {{ fmtLocal(item.collectionFeeLocal, item.moneda) }}
            </template>
            <template v-slot:item.commissionLocal="{ item }">
              {{ fmtLocal(item.commissionLocal, item.moneda) }}
            </template>
            <template v-slot:item.managementFeeLocal="{ item }">
              {{ fmtLocal(item.managementFeeLocal, item.moneda) }}
            </template>
            <!-- Upsell: lo que pagan los usuarios del contrato por sus planes de upsell.
                 Suma al Total MRR del contrato, como los extras. -->
            <template v-slot:item.upsellLocal="{ item }">
              {{ fmtLocal(item.upsellLocal, item.moneda) }}
            </template>
            <template v-slot:item.totalMrrLocal="{ item }">
              <span class="font-weight-medium">{{ rowTotalLocal(item) }}</span>
            </template>
            <template v-slot:item.totalMrrUSD="{ item }">
              <span class="font-weight-medium">USD {{ rowTotalUsd(item).toLocaleString() }}</span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="px-4 pb-3">
          <span class="body-2 font-weight-bold">
            Total: USD {{ detailTotal.toLocaleString() }}
          </span>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para editar metas -->
    <v-dialog v-model="editDialog" max-width="650px" :fullscreen="isMobile">
      <v-card>
        <v-card-title>
          Editar metas — {{ selectedYear }} Q{{ selectedQuarter }}
        </v-card-title>
        <v-card-text>
          <div v-for="pais in paises" :key="pais" class="mb-4">
            <div class="subtitle-2 font-weight-bold mb-2 d-flex align-center">
              <v-icon small left>mdi-flag</v-icon>{{ pais }}
              <v-spacer />
              <span class="primary--text">Total {{ pais }}: USD {{ editTotalPais(pais).toLocaleString() }}</span>
            </div>
            <v-row
              v-for="linea in lineas"
              :key="pais + linea"
              align="center"
              class="my-1"
            >
              <v-col cols="12" sm="6">
                <span class="body-2">{{ linea }}</span>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="editGoals[pais + '||' + linea]"
                  label="Meta USD"
                  type="number"
                  outlined dense hide-details
                  prefix="USD"
                />
              </v-col>
            </v-row>
            <v-divider class="mt-3" />
          </div>
          <div class="d-flex align-center justify-space-between mt-2">
            <span class="subtitle-1 font-weight-bold">Meta total del trimestre</span>
            <span class="subtitle-1 font-weight-bold primary--text">USD {{ editTotalQ.toLocaleString() }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="editDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingGoals" @click="saveGoals">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top>{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script>
import { sortLineas } from '@/utils/linea-order.utils';
import MetasService from '../services/metas.service';
import MRRService from '../services/mrr.service';
import MrrDailyPanel from '../components/MrrDailyPanel.vue';

function normalizarLinea(lineaNegocio, canal) {
  if (lineaNegocio?.startsWith('B2B2C')) return 'B2B2C';
  // Un contrato directo guarda canal 'Directo', no null.
  const viaBroker = !!canal && canal !== 'Directo';
  if (lineaNegocio === 'B2B Employee Benefit') return viaBroker ? 'B2B Employee Benefit Brokers' : 'B2B Employee Benefit Directo';
  if (lineaNegocio === 'B2C') return viaBroker ? 'B2C Brokers' : 'B2C Directo';
  return lineaNegocio;
}

export default {
  name: 'Metas',
  components: { MrrDailyPanel },
  data() {
    const currentYear = new Date().getFullYear();
    const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
    return {
      selectedYear: currentYear,
      selectedQuarter: currentQuarter,
      years: [currentYear - 1, currentYear, currentYear + 1],
      quarters: [
        { text: 'Q1 (Ene–Mar)', value: 1 },
        { text: 'Q2 (Abr–Jun)', value: 2 },
        { text: 'Q3 (Jul–Sep)', value: 3 },
        { text: 'Q4 (Oct–Dic)', value: 4 },
      ],
      headers: [
        { text: 'Línea de negocio', value: 'lineaNegocio', width: '220px' },
        { text: 'Meta Q', value: 'targetUSD', width: '140px' },
        { text: 'MRR Hoy', value: 'actualUSD', width: '140px' },
        { text: 'Progreso', value: 'progress', width: '200px' },
        { text: 'Faltante', value: 'remainingUSD', width: '150px' },
      ],
      detailHeaders: [
        { text: 'Contrato', value: 'contrato', width: '100px', sortable: true },
        { text: 'Empresa', value: 'empresa', sortable: true },
        { text: 'Producto', value: 'producto', sortable: true },
        { text: 'MRR Local', value: 'mrrLocal', align: 'right', width: '130px', sortable: true },
        { text: 'Recaudación', value: 'collectionFeeLocal', align: 'right', width: '120px', sortable: true },
        { text: 'Comisión', value: 'commissionLocal', align: 'right', width: '110px', sortable: true },
        { text: 'Gestión', value: 'managementFeeLocal', align: 'right', width: '110px', sortable: true },
        { text: 'Upsell', value: 'upsellLocal', align: 'right', width: '110px', sortable: true },
        { text: 'Total MRR', value: 'totalMrrLocal', align: 'right', width: '130px', sortable: true },
        { text: 'Total MRR USD', value: 'totalMrrUSD', align: 'right', width: '130px', sortable: true },
      ],
      progressRows: [],
      allMrrRows: [],
      loadingProgress: false,
      loadingDetail: false,
      lineas: [],
      // México se suma a Chile y Perú (spec 084); el orden es el de las secciones.
      paises: ['Chile', 'Perú', 'México'],
      dailyDialog: false,
      // detail modal
      detailDialog: false,
      detailLinea: '',
      detailPais: '',
      detailRows: [],
      // edit modal
      editDialog: false,
      editGoals: {},
      savingGoals: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    // MRR más los extras del contrato (recaudación, comisión y gestión) en USD, que es lo
    // mismo que ahora acumula el cálculo de metas: este total cuadra con la columna
    // "MRR Hoy" de la línea. `totalMrrUSD` viene igual a `mrrUSD` cuando el contrato no
    // tiene extras configurados.
    detailTotal() {
      return this.detailRows.reduce(
        (sum, r) => sum + (r.totalMrrUSD ?? r.mrrUSD ?? 0), 0);
    },
    // Suma en vivo de todas las metas del diálogo (todos los países) para el total del Q.
    editTotalQ() {
      return Object.values(this.editGoals).reduce((sum, v) => sum + (Number(v) || 0), 0);
    },
    totalGeneral() {
      const target = this.paises.reduce((s, p) => s + this.totalesByPais(p).target, 0);
      const actual = this.paises.reduce((s, p) => s + this.totalesByPais(p).actual, 0);
      const remaining = Math.max(0, target - actual);
      return { target, actual, remaining };
    },
    totalGeneralRow() {
      const { target, actual, remaining } = this.totalGeneral;
      return {
        lineaNegocio: 'Total General',
        targetUSD: target,
        actualUSD: actual,
        progress: target > 0 ? Math.round(actual / target * 1000) / 10 : 0,
        remainingUSD: remaining,
      };
    },
  },
  async created() {
    await this.loadLineas();
    await Promise.all([this.load(), this.loadAllMrr()]);
  },
  methods: {
    async loadLineas() {
      const res = await MetasService.getLineas();
      if (res.status === 'ok') this.lineas = sortLineas(res.data);
    },
    async load() {
      this.loadingProgress = true;
      const res = await MetasService.getProgress(this.selectedYear, this.selectedQuarter);
      this.progressRows = res.status === 'ok' ? res.data : [];
      this.loadingProgress = false;
    },
    async loadAllMrr() {
      const res = await MRRService.getDetalleMRR();
      this.allMrrRows = res.status === 'ok' ? res.data : [];
    },
    rowsByPais(pais) {
      const orden = this.lineas;
      return this.progressRows
        .filter(r => r.pais === pais)
        .sort((a, b) => orden.indexOf(a.lineaNegocio) - orden.indexOf(b.lineaNegocio));
    },
    quarterMonths(quarter) {
      const start = (quarter - 1) * 3 + 1;
      return [start, start + 1, start + 2];
    },
    /**
     * Formatea un monto en la moneda de la fila; un extra sin configurar llega en null.
     */
    fmtLocal(val, moneda) {
      if (val == null) return '—';
      return `${Math.round(val).toLocaleString()} ${moneda || ''}`.trim();
    },

    /**
     * MRR más extras de la fila. El backend solo los calcula para el mes en curso, así
     * que cuando no vienen el total es el MRR a secas.
     */
    rowTotalLocal(item) {
      const total = item.totalMrrLocal != null ? item.totalMrrLocal : item.mrrLocal;
      return this.fmtLocal(total, item.moneda);
    },

    rowTotalUsd(item) {
      if (item.totalMrrUSD != null) return item.totalMrrUSD;
      return item.mrrUSD || 0;
    },

    openDetail(item) {
      this.detailLinea = item.lineaNegocio;
      this.detailPais = item.pais;
      const currentMonth = new Date().getMonth() + 1;
      this.detailRows = this.allMrrRows.filter(r => {
        const rowYear = parseInt(r.fecha?.substring(0, 4));
        const rowMonth = parseInt(r.fecha?.substring(5, 7));
        if (rowYear !== this.selectedYear || rowMonth !== currentMonth) return false;
        if (r.pais !== item.pais) return false;
        return normalizarLinea(r.lineaNegocio, r.canal) === item.lineaNegocio;
      });
      this.detailDialog = true;
    },
    async openEditDialog() {
      const res = await MetasService.getGoals(this.selectedYear, this.selectedQuarter);
      const goals = res.status === 'ok' ? res.data : [];
      // Se arma el objeto completo antes de asignarlo a `editGoals` (en vez de
      // ir agregando claves sobre un objeto ya reactivo) porque en Vue 2 las
      // propiedades agregadas después de observar un objeto no son reactivas:
      // los totales en vivo no se actualizaban al escribir en los campos.
      const built = {};
      for (const pais of this.paises) {
        for (const linea of this.lineas) {
          const found = goals.find(g => g.pais === pais && g.lineaNegocio === linea);
          built[`${pais}||${linea}`] = found ? Number(found.targetUSD) : 0;
        }
      }
      this.editGoals = built;
      this.editDialog = true;
    },
    async saveGoals() {
      this.savingGoals = true;
      let allOk = true;
      // Se guardan todas las líneas, incluidas las que bajan a 0 — un
      // guard `> 0` acá impedía persistir la baja a 0 de una meta ya
      // guardada, dejando el valor viejo en el backend indefinidamente.
      for (const [key, target] of Object.entries(this.editGoals)) {
        const [pais, linea] = key.split('||');
        const res = await MetasService.upsert(pais, linea, this.selectedYear, this.selectedQuarter, target);
        if (res.status !== 'ok') allOk = false;
      }
      this.savingGoals = false;
      this.editDialog = false;
      this.snackbarText = allOk ? 'Metas guardadas correctamente' : 'Error al guardar algunas metas';
      this.snackbarColor = allOk ? 'success' : 'error';
      this.snackbar = true;
      await this.load();
    },
    totalesByPais(pais) {
      const rows = this.rowsByPais(pais);
      const target = rows.reduce((s, r) => s + (r.targetUSD || 0), 0);
      const actual = rows.reduce((s, r) => s + (r.actualUSD || 0), 0);
      const remaining = Math.max(0, target - actual);
      return { target, actual, remaining };
    },
    // Suma en vivo de las metas que se están escribiendo en el diálogo, por país.
    editTotalPais(pais) {
      return this.lineas.reduce(
        (sum, linea) => sum + (Number(this.editGoals[`${pais}||${linea}`]) || 0),
        0
      );
    },
    progressPct(actual, target) {
      return target > 0 ? Math.round(actual / target * 1000) / 10 : 0;
    },
    progressColor(pct) {
      if (pct >= 100) return 'success';
      if (pct >= 70) return 'primary';
      if (pct >= 40) return 'warning';
      return 'error';
    },
  },
};
</script>
